import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { Printer, PrinterInsert, PrinterUpdate } from '@/lib/database.types'

export function usePrinters() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const {
    data: printers = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['printers', user?.id],
    queryFn: async (): Promise<Printer[]> => {
      if (!user?.id) return []
      
      const { data, error } = await supabase
        .from('printers')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    },
    enabled: !!user?.id,
    staleTime: 1000 * 30, // 30 seconds for real-time feel
  })

  const createPrinterMutation = useMutation({
    mutationFn: async (printer: Omit<PrinterInsert, 'user_id'>) => {
      if (!user?.id) throw new Error('Not authenticated')
      
      const { data, error } = await supabase
        .from('printers')
        .insert({ ...printer, user_id: user.id })
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printers', user?.id] })
    },
  })

  const updatePrinterMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PrinterUpdate }) => {
      const { data, error } = await supabase
        .from('printers')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printers', user?.id] })
    },
  })

  const deletePrinterMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('printers')
        .delete()
        .eq('id', id)
      
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printers', user?.id] })
    },
  })

  // Get active printer (currently printing)
  const activePrinter = printers.find(p => p.status === 'printing')
  
  // Get idle printers
  const idlePrinters = printers.filter(p => p.status === 'idle')

  return {
    printers,
    activePrinter,
    idlePrinters,
    isLoading,
    error,
    refetch,
    createPrinter: createPrinterMutation.mutateAsync,
    updatePrinter: updatePrinterMutation.mutateAsync,
    deletePrinter: deletePrinterMutation.mutateAsync,
    isCreating: createPrinterMutation.isPending,
    isUpdating: updatePrinterMutation.isPending,
    isDeleting: deletePrinterMutation.isPending,
  }
}
